import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetAllocatedProperties } from '~/hooks/apis/connection-google/useGetAllocatedProperties';

import {
  AllocatedProperty,
  AllocatedPropertyTier,
  GetAllocatedPropertiesParams,
  HintCoverageAccountType,
  downloadHintAllocatedPropertiesCSV,
} from '~/services/ConnectionGoogleService';

const COLUMNS: Array<GridColDef<AllocatedProperty>> = [
  { field: 'id', headerName: 'ID', width: 350 },
  { field: 'name', headerName: 'Name', width: 250 },
  { field: 'goCountry', headerName: 'Country', width: 150 },
  { field: 'tier', headerName: 'Tier', width: 100 },
  {
    field: 'isForced',
    headerName: 'Forced',
    width: 100,
    renderCell: ({ value }) => (value ? 'Yes' : 'No'),
  },
  { field: 'opportunityCount', headerName: 'Opportunity Count', width: 160 },
  {
    field: 'classifiedAt',
    headerName: 'Classified At',
    width: 200,
    valueGetter: (value: string | null) => (value ? new Date(value).toLocaleString() : ''),
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    width: 200,
    valueGetter: (value: string | null) => (value ? new Date(value).toLocaleString() : ''),
  },
];

const DEFAULT_PAGE_SIZE = 10;

interface Props {
  accountType: HintCoverageAccountType;
  /** Opens the property-level hint coverage view when a row is clicked. */
  onPropertySelect?: (property: AllocatedProperty) => void;
}

export default function AllocatedPropertiesSearch({ accountType, onPropertySelect }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [tier, setTier] = useState<AllocatedPropertyTier | ''>('');
  const [forcedFilter, setForcedFilter] = useState<'true' | 'false' | ''>('');
  const [downloading, setDownloading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [submittedFilters, setSubmittedFilters] = useState<GetAllocatedPropertiesParams | null>(null);
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    setSubmittedFilters(null);
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
    setRowCount(0);
  }, [accountType]);

  const { data, isError, isFetching } = useGetAllocatedProperties(submittedFilters);

  // Keep rowCount stable across fetches so the DataGrid does not reset the
  // current page to 0 while a new page query is in-flight (data is undefined).
  useEffect(() => {
    if (data?.total !== undefined) {
      setRowCount(data.total);
    }
  }, [data?.total]);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar('Failed to fetch allocated properties', { variant: 'error' });
    }
  }, [isError, enqueueSnackbar]);

  const handleSearch = () => {
    const newPaginationModel = { page: 0, pageSize: paginationModel.pageSize };
    setPaginationModel(newPaginationModel);
    setSubmittedFilters({
      accountType,
      page: 1,
      limit: newPaginationModel.pageSize,
      search: search || undefined,
      tier: tier || undefined,
      forced: forcedFilter || undefined,
    });
  };

  const handleDownloadCSV = async () => {
    setDownloading(true);
    try {
      // Export follows the page-level account type only — the table's
      // search/tier/forced inputs deliberately do not affect the CSV (GR-4817).
      const blob = await downloadHintAllocatedPropertiesCSV({ accountType });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'gha-allocated-properties.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      enqueueSnackbar('CSV file downloaded successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to download CSV file', { variant: 'error' });
      console.error('Failed to download allocated properties CSV', error);
    } finally {
      setDownloading(false);
    }
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
    if (submittedFilters) {
      setSubmittedFilters({
        ...submittedFilters,
        page: model.page + 1,
        limit: model.pageSize,
      });
    }
  };

  return (
    <Box mt={4}>
      <PageSubheader title="Allocated Properties" />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Property ID or Name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="tier-label">Tier</InputLabel>
              <Select labelId="tier-label" value={tier} label="Tier" onChange={(e) => setTier(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="HIGH">High</MenuItem>
                <MenuItem value="MEDIUM">Medium</MenuItem>
                <MenuItem value="LOW">Low</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="forced-label">Forced</InputLabel>
              <Select
                labelId="forced-label"
                value={forcedFilter}
                label="Forced"
                onChange={(e) => setForcedFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 1.5 }}>
            <Stack height="100%" justifyContent="center">
              <Button variant="contained" onClick={handleSearch} fullWidth>
                Search
              </Button>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 1.5 }}>
            <Stack height="100%" justifyContent="center">
              <Button
                variant="outlined"
                onClick={handleDownloadCSV}
                disabled={downloading}
                startIcon={downloading ? <CircularProgress size={16} /> : undefined}
                fullWidth
              >
                {downloading ? 'Downloading...' : 'Download CSV'}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      <Box mt={2}>
        <DataGrid
          rows={data?.data ?? []}
          columns={COLUMNS}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          loading={isFetching}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 50, 100]}
          slots={{ pagination: GridPagination }}
          onRowClick={onPropertySelect ? (params) => onPropertySelect(params.row) : undefined}
          sx={onPropertySelect ? { '& .MuiDataGrid-row': { cursor: 'pointer' } } : undefined}
        />
      </Box>
    </Box>
  );
}
